import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CardSetTransformationService, CoreService, DataService, DialogService, FormConfigurationService, SaveDataRequest, SaveFormEvent, SpinnerService } from 'dspx-core';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { NAV_TAB_NUMBERS, PAGE_INDEX, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES, SECTION_INDEX, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { finalize, take } from 'rxjs';

@Component({
  selector: 'app-care-allowance-rate',
  templateUrl: './care-allowance-rate.component.html',
  styleUrl: './care-allowance-rate.component.scss'
})
export class CareAllowanceRateComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;

  templateId: number = TEMPLATES.careAllowanceRate;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';

  private exitRoutePrefix = '';
  private isPendingChangePreview: boolean;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService,
    private formConfigurationService: FormConfigurationService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {
      displayBtnSave: () => !this.isPendingChangePreview
    };

    this.coreService.conditionFunctions = {
      alwaysReadOnly: () => true,
      isPendingChangePreview: () => this.isPendingChangePreview,
      showPlacementType: () => this.isBaseRateType(),
      showLevel: () => this.isBaseRateType() || this.isFirstSixMonthsRateType()
    }

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getCareAllowanceRateConfigAndResponse;
        this.id = id;
      }
    });

    if (this.router.url.includes(ROUTE_CONSTANTS.careAllowanceRate.prefixViewPending)) {
      this.exitRoutePrefix = ROUTE_CONSTANTS.careAllowanceRate.list.ratesPendingApproval;
      this.isPendingChangePreview = true;
    }
    else {
      this.exitRoutePrefix = ROUTE_CONSTANTS.careAllowanceRate.list.ratesApproved;
      this.isPendingChangePreview = false;
    }
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded($event: any): void {
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.exit();
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    this.exit(true);
  }

  private exit(ignoreUnsavedChanges = false): void {
    this.navigate([this.exitRoutePrefix], ignoreUnsavedChanges);
  }

  private saveData(request: SaveDataRequest) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.careAllowanceRate.save, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.saveCareAllowanceRate.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }

  private getRateType(): string {
    if (this.baseForm?.model?.form?.tabs != null) {
      return this.formConfigurationService.getQuestionResponse(
        this.baseForm.model.form.tabs,
        'tab' + NAV_TAB_NUMBERS.careAllowanceRateForm.rateDetails,
        'page' + PAGE_NUMBERS.careAllowanceRateForm.rateDetails,
        PAGE_INDEX.noRepeater,
        SECTION_CODES.careAllowanceRateForm.rateDetails,
        SECTION_INDEX.noRepeater,
        QUESTION_CODES.careAllowanceRateForm.rateType
      );
    }

    return null;
  }

  private isBaseRateType(): boolean {
    const rateType = this.getRateType();

    return rateType === REFERENCE_DATA_IDS.careAllowanceRateType.base;
  }

  private isFirstSixMonthsRateType(): boolean {
    const rateType = this.getRateType();

    return rateType === REFERENCE_DATA_IDS.careAllowanceRateType.firstSixMonths;
  }
}
